// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#zombie-app-detection
import * as React from 'react';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { type AppUsageSummaryItem } from '@greenlight/shared';

/**
 * When a person last actually used the app. Read from the usage summary's
 * `last_active_at`, which is the newest authenticated access *ever* recorded —
 * unlike the Usage column beside it, this is not bounded by the window.
 *
 * Three outcomes, deliberately distinct: no summary row means the read has not
 * resolved (dash, not "never"); a row with no timestamp means the app has
 * genuinely never been used; otherwise coarse elapsed time, matching Last
 * Updated. Exact timestamps belong in the app-detail view.
 */
export function LastUsedSignal({
  usage,
}: {
  usage: AppUsageSummaryItem | undefined;
}): React.JSX.Element {
  if (!usage) return <span className="text-muted-foreground">—</span>;
  if (usage.last_active_at === null) {
    return (
      <span
        className="text-muted-foreground"
        title="No authenticated access has ever been recorded for this app"
      >
        Never
      </span>
    );
  }
  return <span className="text-muted-foreground">{relativeTime(usage.last_active_at)}</span>;
}

/**
 * Sort key for the Last Used column. An app with no summary row orders below one
 * that has genuinely never been used, so "not loaded" and "never" never collapse
 * into the same position — the same distinction the Usage sort makes.
 */
export function lastUsedSortValue(usage: AppUsageSummaryItem | undefined): number {
  if (!usage) return -1;
  return usage.last_active_at === null ? 0 : new Date(usage.last_active_at).getTime();
}

/**
 * Shown beside the app's status when the inactivity scan has flagged it and the
 * org's auto-pause step will pause it. It is an overlay on the status, not a
 * status of its own: an app is `Active` *and* scheduled to pause, which is
 * exactly the window in which someone can still do something about it.
 */
export function ScheduledToPauseBadge(): React.JSX.Element {
  return (
    <StatusBadge
      variant="degraded"
      title="No recent activity — this app will be paused unless it is used again or confirmed as still needed"
    >
      Scheduled to pause
    </StatusBadge>
  );
}
